import { useQuery, useApolloClient, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useLogin } from 'features/Login/hooks'
import { usePlace } from 'features/Places/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canRead } from 'features/Users/utils'
import { SegmentsPagination } from 'features/Segments/views/Index/Pagination'
import { GET_SEGMENTS_WITH_PAGINATION } from 'features/Segments/queries-legacy'
import { GTSegmentsQueryInput, GTSegmentsResponse } from 'gql/types.generated'
import { resolvers as segmentsResolvers } from 'resolvers/segments'
import { BOOLEAN } from 'utils/boolean'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { SegmentsList } from './List'
import { SegmentsFilters } from './Filters'
import { useCompanyAutocomplete } from 'features/Companies/hooks'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'segments.helmet',
    defaultMessage: 'Segments | Nexbus Admin'
  }
})

const SegmentsView = (): TSFixMe => {
  const location = useLocation()
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(segmentsResolvers)
  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content

  const {
    hasPricing = '',
    hasDuration = '',
    company = '',
    origin = '',
    destination = '',
    page = '1'
  } = getQueries(location.search)
  const pageNb = parseInt(page, 10)

  const companyData = useCompanyAutocomplete(company, { skip: !company })
  const originPlace = usePlace(origin, { skip: !origin })
  const destinationPlace = usePlace(destination, { skip: !destination })

  const filtersApplied: GTSegmentsQueryInput = filterEmptyValue({
    companyID: company,
    originID: origin,
    destinationID: destination,
    hasPricing: undefined,
    hasDuration: undefined
  })

  if (hasPricing && (hasPricing === BOOLEAN.TRUE || hasPricing === BOOLEAN.FALSE)) {
    filtersApplied.hasPricing = hasPricing === BOOLEAN.TRUE
  }

  if (hasDuration && (hasDuration === BOOLEAN.TRUE || hasDuration === BOOLEAN.FALSE)) {
    filtersApplied.hasDuration = hasDuration === BOOLEAN.TRUE
  }

  const { loading, error, data, refetch, networkStatus } = useQuery<{ segmentsWithPagination: GTSegmentsResponse }>(
    GET_SEGMENTS_WITH_PAGINATION,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      variables: {
        pagination: {
          size: LIMIT,
          page: pageNb
        },
        input: {
          ...filtersApplied
        }
      }
    }
  )

  const filters = {
    hasPricing,
    hasDuration,
    company: companyData,
    origin: originPlace,
    destination: destinationPlace
  }

  useEffect(() => {
    refetch()
  }, [])

  if (!canRead(FEATURES.SEGMENT, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SEGMENT} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="segments.error.description"
            defaultMessage="There was an error while loading your segments"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="segments.error.action" defaultMessage="Reload your segments" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.segmentsWithPagination.items) && isEmpty(filtersApplied)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="segments.empty.description" defaultMessage="You don&#39;t have any segments yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/routes/new" kind="primary">
            <FormattedMessage id="segments.empty.action" defaultMessage="Add your first route" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.segmentsWithPagination.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="segments.empty.filters.description"
            defaultMessage="There were no segments found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/segments" kind="primary">
            <FormattedMessage id="segments.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <SegmentsList segments={data?.segmentsWithPagination.items || []} />
        <SegmentsPagination
          page={pageNb}
          limit={LIMIT}
          totalItems={data?.segmentsWithPagination.total || 0}
          currentItemsCount={data?.segmentsWithPagination.items.length || 0}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="segments.header.label" defaultMessage="Segments" />
        </Heading.H3>
      </ContentHeader>
      <ContentBody>
        <SegmentsFilters filters={filters} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(SegmentsView)
